// ============================================================
// useLiveRun — single-run hydration + realtime fan-out for the
// shared canvas. Powers Studio (and any future canvas wrapper)
// with the actual sq_workspace_runs row, sq_workspace_sections
// for that run, and sq_workspace_agent_events for that run, all
// synchronized via Supabase Realtime.
//
// Why a dedicated hook:
//   - useStudioRuns gives the project-wide run list
//   - useWorkspaceEvents gives the project-wide event list
//   - useSite/useSitePages/useSiteBlocks are Site Builder data
//
// None of those answer "what is THIS specific run's content
// progress right now?" The canvas needs that to switch from a
// local-timeout demo timeline to a real backend-driven render.
//
// Backend reuse:
//   - Hydrates via /api/workspace/get(workspace_id) which already
//     returns sections + the latest run.
//   - Subscribes to postgres_changes on:
//       sq_workspace_runs           (UPDATE filter id=eq.<runId>)
//       sq_workspace_sections       (INSERT filter run_id=eq.<runId>)
//       sq_workspace_agent_events   (INSERT filter run_id=eq.<runId>)
//
// Honest fallback:
//   - In non-live mode the hook is a no-op that returns empty
//     state. The canvas renders its existing local demo timeline
//     when liveRun is null.
//   - Auth-gated failures degrade silently — the canvas falls
//     back to its visual demo timeline so the operator never sees
//     a tokenless empty canvas.
// ============================================================

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js'
import { getRuntime } from './_mode'
import { api, ApiError, isAuthError } from '../api/client'
import {
  captureHookFailure,
  recordHookOutcome,
} from '../observability/sentry'

export interface LiveRunRow {
  id: string
  workspace_id: string
  mode: string
  prompt: string
  status: 'pending' | 'running' | 'done' | 'error' | 'cancelled' | string
  progress: number
  provider: string
  provider_job_id: string | null
  started_at: string | null
  completed_at: string | null
  created_at: string
  error_json: Record<string, unknown> | null
  output_json: Record<string, unknown> | null
}

export interface LiveSection {
  id: string
  workspace_id: string
  run_id: string | null
  section_order: number
  section_type: string
  title: string | null
  content_json: Record<string, unknown>
  source_agent: string | null
  created_at: string
}

export interface LiveAgentEvent {
  id: string
  workspace_id: string
  run_id: string | null
  agent_name: string
  event_type: string
  message: string
  created_at: string
}

export interface UseLiveRunResult {
  /** Live run state. null until the wrapper calls activate(). */
  run: LiveRunRow | null
  /** Sections produced by this run, ordered by section_order. */
  sections: LiveSection[]
  /** Agent activity events for this run, ordered by created_at. */
  events: LiveAgentEvent[]
  /** True after the first hydration completes. */
  ready: boolean
  /** Real backend error (not auth). null in clean states. */
  error: string | null
  /**
   * Bind the hook to a specific (workspace, run) pair. Subsequent
   * calls swap the binding atomically. Pass null to unbind.
   */
  activate: (binding: { workspaceId: string; runId: string } | null) => void
  /** Force a re-hydration (e.g. after a refresh button). */
  refresh: () => Promise<void>
}

export function useLiveRun(): UseLiveRunResult {
  const [binding, setBinding] = useState<{ workspaceId: string; runId: string } | null>(null)
  const [run, setRun] = useState<LiveRunRow | null>(null)
  const [sections, setSections] = useState<LiveSection[]>([])
  const [events, setEvents] = useState<LiveAgentEvent[]>([])
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hydrate = useCallback(async () => {
    if (!binding) {
      setRun(null)
      setSections([])
      setEvents([])
      setReady(false)
      setError(null)
      return
    }
    const { live } = getRuntime()
    if (!live) {
      setReady(true)
      recordHookOutcome('useLiveRun', 'demo_fallback', { workspaceId: binding.workspaceId })
      return
    }
    setError(null)
    try {
      // Single round-trip: workspace + sections + latest_run.
      const result = await api.workspace.get(binding.workspaceId)
      const allSections = (result.sections as LiveSection[] | null) ?? []
      const runRow = (result.latest_run as LiveRunRow | null) ?? null
      // We only render sections for THIS run id. Other runs in the
      // same workspace stay hidden so the canvas never bleeds across
      // runs.
      setSections(allSections.filter((s) => s.run_id === binding.runId))
      // /api/workspace/get returns the latest run, which is usually
      // ours when we just dispatched. If it isn't, leave run null so
      // the canvas shows the local timeline rather than a stale row.
      setRun(runRow && runRow.id === binding.runId ? runRow : null)
      setReady(true)
      recordHookOutcome('useLiveRun', 'live_success', {
        route: '/api/workspace/get',
        rows: allSections.length,
      })
    } catch (err) {
      if (isAuthError(err)) {
        // Pre-sign-in state — no system fault, no banner.
        setReady(true)
        recordHookOutcome('useLiveRun', 'demo_fallback', { reason: 'auth_required' })
        return
      }
      const apiErr = err as ApiError
      setError(apiErr?.message || 'live_run_hydrate_failed')
      captureHookFailure('useLiveRun', 'live_error', err, {
        route: '/api/workspace/get',
        code: apiErr?.code,
      })
    }
  }, [binding])

  useEffect(() => {
    void hydrate()
  }, [hydrate])

  // Realtime fan-out — sections + events + run row updates.
  useEffect(() => {
    if (!binding) return
    const { live, supabase } = getRuntime()
    if (!live || !supabase) return

    let cancelled = false
    const ch = subscribeRealtime(
      supabase,
      binding.workspaceId,
      binding.runId,
      {
        onSection(row) {
          if (cancelled) return
          setSections((prev) => mergeOrdered(prev, row))
        },
        onEvent(row) {
          if (cancelled) return
          setEvents((prev) => (prev.some((e) => e.id === row.id) ? prev : [...prev, row]))
        },
        onRunUpdate(row) {
          if (cancelled) return
          setRun(row)
        },
        onError(err) {
          captureHookFailure('useLiveRun', 'realtime_failure', err, {
            workspaceId: binding.workspaceId,
            runId: binding.runId,
          })
        },
      },
    )

    return () => {
      cancelled = true
      try {
        void supabase.removeChannel(ch)
      } catch {
        // Channel cleanup failures are not user-visible.
      }
    }
  }, [binding])

  const activate = useCallback(
    (next: { workspaceId: string; runId: string } | null) => {
      // Reset client-side state on every activation so a new run
      // starts visually fresh.
      setSections([])
      setEvents([])
      setRun(null)
      setReady(false)
      setError(null)
      setBinding(next)
    },
    [],
  )

  return useMemo(
    () => ({
      run,
      sections,
      events,
      ready,
      error,
      activate,
      refresh: hydrate,
    }),
    [run, sections, events, ready, error, activate, hydrate],
  )
}

// ----------------------------------------------------------------
// Realtime helpers
// ----------------------------------------------------------------

interface RealtimeHandlers {
  onSection: (row: LiveSection) => void
  onEvent: (row: LiveAgentEvent) => void
  onRunUpdate: (row: LiveRunRow) => void
  onError: (err: unknown) => void
}

function subscribeRealtime(
  supabase: SupabaseClient,
  workspaceId: string,
  runId: string,
  h: RealtimeHandlers,
): RealtimeChannel {
  return supabase
    .channel(`live-run-${runId}`)
    .on(
      'postgres_changes' as never,
      {
        event: 'INSERT',
        schema: 'public',
        table: 'sq_workspace_sections',
        filter: `workspace_id=eq.${workspaceId}`,
      },
      (payload: { new: LiveSection }) => {
        if (payload.new && payload.new.run_id === runId) {
          h.onSection(payload.new)
        }
      },
    )
    .on(
      'postgres_changes' as never,
      {
        event: 'INSERT',
        schema: 'public',
        table: 'sq_workspace_agent_events',
        filter: `workspace_id=eq.${workspaceId}`,
      },
      (payload: { new: LiveAgentEvent }) => {
        if (payload.new && payload.new.run_id === runId) {
          h.onEvent(payload.new)
        }
      },
    )
    .on(
      'postgres_changes' as never,
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'sq_workspace_runs',
        filter: `id=eq.${runId}`,
      },
      (payload: { new: LiveRunRow }) => {
        if (payload.new) h.onRunUpdate(payload.new)
      },
    )
    .subscribe((status: string) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        h.onError(new Error(`live-run channel ${status}`))
      }
    })
}

function mergeOrdered(prev: LiveSection[], row: LiveSection): LiveSection[] {
  if (prev.some((s) => s.id === row.id)) return prev
  const next = [...prev, row]
  next.sort((a, b) => a.section_order - b.section_order)
  return next
}
